// Storage + fetch wrapper for kiosk (shared-device) e-canteen sessions.
// Deliberately separate from utils/api.js's localStorage 'token' slot — a
// kiosk device may sit right next to (or share a browser profile with) a
// real logged-in staff/parent session, so the two must never collide.
// sessionStorage also means the kiosk session dies with the tab, a useful
// extra safety property for a shared device.
import { API_URL } from './api';

const TOKEN_KEY = 'campusio_kiosk_token';
const EXPIRES_KEY = 'campusio_kiosk_expires_at';
const NAME_KEY = 'campusio_kiosk_student_first_name';

export const kioskSession = {
  set({ session_token, expires_at, student_first_name }) {
    sessionStorage.setItem(TOKEN_KEY, session_token);
    sessionStorage.setItem(EXPIRES_KEY, expires_at);
    sessionStorage.setItem(NAME_KEY, student_first_name || '');
  },
  clear() {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(EXPIRES_KEY);
    sessionStorage.removeItem(NAME_KEY);
  },
  getToken() {
    return sessionStorage.getItem(TOKEN_KEY);
  },
  getExpiresAt() {
    return sessionStorage.getItem(EXPIRES_KEY);
  },
  getStudentFirstName() {
    return sessionStorage.getItem(NAME_KEY);
  },
};

async function request(endpoint, options = {}) {
  const token = kioskSession.getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };
  const response = await fetch(`${API_URL}${endpoint}`, { ...options, headers });

  let data = null;
  const contentType = response.headers.get('Content-Type') || '';
  if (response.status !== 204 && contentType.includes('application/json')) {
    try { data = await response.json(); } catch (_) { data = null; }
  }

  if (!response.ok) {
    const error = new Error(data?.detail || 'Request failed');
    error.status = response.status;
    throw error;
  }
  return data;
}

export const kioskApi = {
  scan: (card_number) => request('/api/kiosk/scan', { method: 'POST', body: JSON.stringify({ card_number }) }),
  verifyPin: (pending_token, pin) => request('/api/kiosk/verify-pin', { method: 'POST', body: JSON.stringify({ pending_token, pin }) }),
  logout: (session_token) => request('/api/kiosk/logout', { method: 'POST', body: JSON.stringify({ session_token }) }).catch(() => {}),
  checkSession: (session_token) => request(`/api/kiosk/session?session_token=${encodeURIComponent(session_token)}`),
  get: (endpoint, params) => {
    let url = endpoint;
    if (params && Object.keys(params).length) {
      const filtered = {};
      Object.entries(params).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) filtered[k] = v; });
      const qs = new URLSearchParams(filtered).toString();
      if (qs) url += `?${qs}`;
    }
    return request(url);
  },
  post: (endpoint, body) => request(endpoint, { method: 'POST', body: JSON.stringify(body || {}) }),
};
